import { CrownFilled } from '@ant-design/icons';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ym from 'react-yandex-metrika';

import appPersonContextConnector from 'context/AppPersonContext/appPersonContextConnector';
import { getSportVokrugPlusPlansUrl } from 'helpers/subscriptionUrl';
import { urlRootPath } from 'helpers/urlPath';
import { AUTH_URL_PART } from 'pages/Auth/URL';
import { SPORT_URL_PART } from 'pages/Sport/_constants/SPORT_URLS';
import { getAuthPageUrl } from 'utils/authorization';
import {
  SPORT_VOKRUG_PLUS_PROMO_QUERY_PARAM,
  SPORT_VOKRUG_PLUS_SUBSCRIPTION_ANCHOR,
} from 'constants/SUBSCRIPTION';

import SportVokrugPlusPromoModal from './_SportVokrugPlusPromoModal';
import { PromoButton } from './styles';
import { SPORT_VOKRUG_PLUS_PROMO_TRANSLATION_NAMESPACE } from './translations';

export const getSportVokrugPlusPromoBackUrl = (current_url) => {
  const current = new URL(current_url);
  const existing_back_url = current.searchParams.get('back_url');
  const auth_path = urlRootPath(SPORT_URL_PART, AUTH_URL_PART);
  let target = current;

  if (existing_back_url) {
    try {
      target = new URL(existing_back_url, current.origin);
    } catch (error) {
      target = current;
    }
  } else if (current.pathname.replace(/\/+$/, '') === auth_path.replace(/\/+$/, '')) {
    target = new URL('/', current.origin);
  }

  target.searchParams.set(SPORT_VOKRUG_PLUS_PROMO_QUERY_PARAM, 'open');

  return target.toString();
};

export const hasActiveSportVokrugPlusSubscription = (subscription) => {
  const status = subscription?.status;

  return status?.sid === 'active'
    || status?.sid === 'payment_processing'
    || (status?.sid === 'canceled' && !status.expired);
};

const sendMetrikaGoal = (metrika_allowed, goal) => {
  if (!metrika_allowed) return;

  try {
    ym('reachGoal', goal);
  } catch (error) {
    // Yandex Metrika goal tracking failed
  }
};

const SportVokrugPlusPromo = ({
  is_guest,
  metrika_allowed,
  sport_vokrug_plus_subscription,
  sport_vokrug_plus_subscription_error,
  sport_vokrug_plus_subscription_loading,
  user,
}) => {
  const { t } = useTranslation(SPORT_VOKRUG_PLUS_PROMO_TRANSLATION_NAMESPACE);
  const [is_modal_open, setIsModalOpen] = useState(false);
  const [should_scroll_to_plans, setShouldScrollToPlans] = useState(false);

  useEffect(() => {
    const current = new URL(window.location.href);
    const should_open_after_auth = current.searchParams.get(SPORT_VOKRUG_PLUS_PROMO_QUERY_PARAM) === 'open';

    if (is_guest || !should_open_after_auth) return;

    setIsModalOpen(true);
    current.searchParams.delete(SPORT_VOKRUG_PLUS_PROMO_QUERY_PARAM);
    window.history.replaceState(
      window.history.state,
      '',
      `${current.pathname}${current.search}${current.hash}`,
    );
  }, [is_guest]);

  const handlePromoClick = () => {
    sendMetrikaGoal(metrika_allowed, 'click-button-on-header-sv-plus');

    if (is_guest) {
      window.location.href = getAuthPageUrl(getSportVokrugPlusPromoBackUrl(window.location.href));
      return;
    }

    setIsModalOpen(true);
  };

  const plans_url = user?.auser_id
    ? getSportVokrugPlusPlansUrl(user.auser_id)
    : null;

  const handleChoosePlan = () => {
    if (!plans_url) return;

    sendMetrikaGoal(metrika_allowed, 'click-button-choice-plan-sv-plus');

    const plans_location = new URL(plans_url, window.location.origin);
    const current_path = window.location.pathname.replace(/\/+$/, '');
    const plans_path = plans_location.pathname.replace(/\/+$/, '');

    if (current_path !== plans_path) {
      window.location.href = plans_url;
      return;
    }

    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}#${SPORT_VOKRUG_PLUS_SUBSCRIPTION_ANCHOR}`,
    );
    setShouldScrollToPlans(true);
    setIsModalOpen(false);
  };

  const handleModalClose = () => {
    sendMetrikaGoal(metrika_allowed, 'click-close-modal-sv-plus');
    setIsModalOpen(false);
  };

  const handleModalVisibilityChange = (is_open) => {
    if (is_open || !should_scroll_to_plans) return;

    document.getElementById(SPORT_VOKRUG_PLUS_SUBSCRIPTION_ANCHOR)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    setShouldScrollToPlans(false);
  };

  if (!is_guest && sport_vokrug_plus_subscription_loading) return null;
  if (!sport_vokrug_plus_subscription_error && hasActiveSportVokrugPlusSubscription(sport_vokrug_plus_subscription)) return null;

  return (
    <>
      <PromoButton
        type="button"
        onClick={handlePromoClick}
        data-testid="sport-vokrug-plus-promo-button"
        aria-label={t('ariaLabel')}
      >
        <CrownFilled
          aria-hidden="true"
          data-testid="sport-vokrug-plus-promo-icon"
        />
        <span data-full-label>{t('button')}</span>
      </PromoButton>

      <SportVokrugPlusPromoModal
        is_open={is_modal_open}
        is_plan_available={Boolean(plans_url)}
        on_after_open_change={handleModalVisibilityChange}
        on_choose_plan={handleChoosePlan}
        on_close={handleModalClose}
      />
    </>
  );
};

SportVokrugPlusPromo.propTypes = {
  is_guest: PropTypes.bool.isRequired,
  metrika_allowed: PropTypes.bool.isRequired,
  sport_vokrug_plus_subscription: PropTypes.shape({
    status: PropTypes.shape({
      expired: PropTypes.bool,
      sid: PropTypes.string,
    }),
  }),
  sport_vokrug_plus_subscription_error: PropTypes.object,
  sport_vokrug_plus_subscription_loading: PropTypes.bool,
  user: PropTypes.shape({
    auser_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
};

SportVokrugPlusPromo.defaultProps = {
  sport_vokrug_plus_subscription: null,
  sport_vokrug_plus_subscription_error: null,
  sport_vokrug_plus_subscription_loading: false,
};

export { SportVokrugPlusPromo };

export default appPersonContextConnector([
  'is_guest',
  'metrika_allowed',
  'user',
  'sport_vokrug_plus_subscription',
  'sport_vokrug_plus_subscription_error',
  'sport_vokrug_plus_subscription_loading',
])(
  SportVokrugPlusPromo,
);
